import { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { DOCUMENT_CATEGORIES } from '../types';
import { LibraryItem } from '../components/library-list/types';
import { selectAllCompanies } from '../../../slices/companies/companiesSlice';
import { NWClient } from '../../../client/NWClient';
import {
  fetchDocumentTemplates,
  clearDocumentTemplates,
} from '../../../slices/document-templates/documentTemplatesSlice';
import { fetchDisclaimerInfo } from '../../../slices/disclaimer/disclaimerSlice';
import { fetchEmployees } from '../../../slices/employees/employeesSlice';
import { fetchCustomers } from '../../../slices/customers/customersSlice';
import { fetchSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { fetchBusinessProfileContactById } from '../../../slices/business-profile/businessProfileSlice';

import { RootState } from '../../../store';
import { LibraryRegion, LibraryType, useLibraryQuery } from './useLibraryQuery';

type SearchParams = {
  category?: DOCUMENT_CATEGORIES;
  region?: string;
  name?: string;
};

/** API query param of the Employment requirement filter (sent as a repeated param). */
const EMPLOYEE_DOC_REQUIREMENTS_PARAM = 'employee_doc_requirements';

/**
 * Builds the document-library query. The requirement filter is sent as repeated params
 * (`?employee_doc_requirements=all&employee_doc_requirements=casual`), not comma-separated,
 * so a URLSearchParams instance is passed to axios as is.
 */
const buildDocumentLibraryQuery = (
  params: SearchParams,
  employeeDocRequirements: string[]
): URLSearchParams => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });
  employeeDocRequirements.forEach((requirement) =>
    query.append(EMPLOYEE_DOC_REQUIREMENTS_PARAM, requirement)
  );
  return query;
};

export const useLibrary = () => {
  const currentUser = useAppSelector(selectUser);
  const company = useAppSelector(selectAllCompanies)[0];
  const dispatchCustom = useAppDispatch();
  const employeesLoading = useAppSelector((state: RootState) => state.employees.status);
  const customersLoading = useAppSelector((state: RootState) => state.customers.status);
  const suppliersLoading = useAppSelector((state: RootState) => state.suppliers.status);
  const businessDetailsLoading = useAppSelector((state: RootState) => state.businessProfile.status);
  /*
   * Library type, category, workflow, card/list view, region, the committed search and the
   * Employment requirement filter live in
   * the URL query (see useLibraryQuery), so the browser Back button from a library item details
   * page restores exactly the same view.
   */
  const {
    libraryType,
    category: activeTab,
    workflow,
    cardView,
    region,
    search: searchString,
    employeeDocRequirements,
    updateLibraryQuery,
  } = useLibraryQuery(true);
  // A primitive key, so a new array instance for the same selection does not refetch the list.
  const employeeDocRequirementsKey = employeeDocRequirements.join(',');
  const [documentLibraryItems, setDocumentLibraryItems] = useState<LibraryItem[]>([]);
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [disclaimerMessage, setDisclaimerMessage] = useState<boolean>(false);
  const [employmentDataMessage, setEmploymentDataMessage] = useState<boolean>(false);
  const [searchStringInputValue, setSearchStringInputValue] = useState(() => searchString);
  const [searchCleared, setSearchCleared] = useState(false);
  const libraryItems = [...documentLibraryItems].filter(
    (el) => el.name !== '0. Policy and Procedure Confirmation'
  );

  // The search value written to the URL by this component, to tell it apart from a change
  // caused by the browser history (Back / Forward), which must be shown in the input.
  const lastCommittedSearchRef = useRef(searchString);
  useEffect(() => {
    if (searchString !== lastCommittedSearchRef.current) {
      lastCommittedSearchRef.current = searchString;
      startTransition(() => setSearchStringInputValue(searchString));
    }
  }, [searchString]);

  const commitSearchString = useCallback(
    (value: string) => {
      lastCommittedSearchRef.current = value;
      // Typing must not create a history entry per keystroke.
      updateLibraryQuery({ search: value }, { replace: true });
    },
    [updateLibraryQuery]
  );

  const handleTabChange = (tab: DOCUMENT_CATEGORIES) => {
    updateLibraryQuery({ category: tab, workflow: null });
  };

  const setWorkflow = useCallback(
    (value: string | null) => {
      updateLibraryQuery({ workflow: value || null });
    },
    [updateLibraryQuery]
  );

  /** A workflow can be chosen only in the card view, so the list view drops it. */
  const setCardView = useCallback(
    (value: boolean, options?: { replace?: boolean }) => {
      updateLibraryQuery({ cardView: value, ...(value ? {} : { workflow: null }) }, options);
    },
    [updateLibraryQuery]
  );

  const setRegion = useCallback(
    (value: LibraryRegion) => {
      updateLibraryQuery({ region: value });
    },
    [updateLibraryQuery]
  );

  /** Switching between the Business Kitz and Own libraries starts from the categories. */
  const setLibraryType = useCallback(
    (value: LibraryType) => {
      updateLibraryQuery({
        libraryType: value,
        category: 'All',
        workflow: null,
        cardView: true,
        search: '',
      });
    },
    [updateLibraryQuery]
  );

  const getSearchParams = useCallback(
    (basicParams: SearchParams) => {
      return searchString ? { ...basicParams, name: searchString } : basicParams;
    },
    [searchString]
  );

  const retrieveTemplates = useCallback(() => {
    if (libraryType === 'businessKitz') {
      const basicParams = activeTab === 'All' ? { region } : { category: activeTab, region };
      const requirements = employeeDocRequirementsKey ? employeeDocRequirementsKey.split(',') : [];
      NWClient.list(
        'document-library',
        buildDocumentLibraryQuery(getSearchParams(basicParams), requirements)
      )
        .then((res: LibraryItem[]) => {
          const templatesSortedByName = [...res].sort((a, b) => a.name.localeCompare(b.name));
          setDocumentLibraryItems(templatesSortedByName);
        })
        .finally(() => {
          setPendingStatus(false);
        });
    } else {
      const basicParams = activeTab === 'All' ? {} : { category: activeTab };
      dispatchCustom(
        fetchDocumentTemplates({
          data: { ...getSearchParams(basicParams), user: currentUser?.id },
        })
      )
        .unwrap()
        .finally(() => {
          setPendingStatus(false);
        });
    }
  }, [
    libraryType,
    activeTab,
    region,
    employeeDocRequirementsKey,
    getSearchParams,
    dispatchCustom,
    currentUser?.id,
  ]);

  const resetSearchFilter = useCallback(() => {
    commitSearchString('');
    setSearchStringInputValue('');
    setSearchCleared(true);
    setPendingStatus(true);
    //retrieveTemplates();
  }, [commitSearchString]);

  const setSearchStringValue = useCallback(() => {
    if (!pendingStatus) {
      commitSearchString(searchStringInputValue);
    }
  }, [pendingStatus, searchStringInputValue, commitSearchString]);

  /*const getDocuments = useCallback(() => {
    if (currentUser.id) {
      dispatchCustom(
        fetchDocuments({
          data: {
            user: currentUser?.id,
          },
        })
      );
    }
  }, [dispatch, currentUser?.id]);*/

  const handleRetrieveTemplates = useCallback(() => {
    startTransition(() => {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setPendingStatus(true);
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setDocumentLibraryItems([]);
    });
    dispatchCustom(clearDocumentTemplates());
    retrieveTemplates();
  }, [dispatchCustom, retrieveTemplates]);

  const getDisclaimerInfo = useCallback(() => {
    dispatchCustom(fetchDisclaimerInfo());
  }, [dispatchCustom]);

  const handleSearch = useCallback(() => {
    if (searchString && searchString.trim().length >= 3) {
      startTransition(() => {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setPendingStatus(true);
      });
      // Search results are shown as a list (no extra history entry: it follows the search).
      setCardView(false, { replace: true });
      retrieveTemplates();
    }
  }, [searchString, retrieveTemplates, setCardView]);

  /*useEffect(() => {
    getDocuments();
  }, [getDocuments]);*/

  useEffect(() => {
    getDisclaimerInfo();
  }, [getDisclaimerInfo]);

  /*useEffect(() => {
    if (location.pathname === ROUTES.DOCUMENT_LIBRARY) {

      setLibraryType('businessKitz');
    }
  }, [location.pathname]);*/

  useEffect(() => {
    handleRetrieveTemplates();
  }, [handleRetrieveTemplates]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  useEffect(() => {
    if (employeesLoading === 'idle') {
      dispatchCustom(fetchEmployees({ company_id: company.id! }));
    }
  }, [company, dispatchCustom, employeesLoading]);

  useEffect(() => {
    if (customersLoading === 'idle') {
      dispatchCustom(fetchCustomers({ company_id: company.id! }));
    }
  }, [company, dispatchCustom, customersLoading]);

  useEffect(() => {
    if (suppliersLoading === 'idle') {
      dispatchCustom(fetchSuppliers({ company_id: company.id!, page_size: 100 }));
    }
  }, [company, dispatchCustom, suppliersLoading]);

  useEffect(() => {
    if (businessDetailsLoading === 'idle') {
      dispatchCustom(fetchBusinessProfileContactById({ id: company.id! }));
    }
  }, [company, dispatchCustom, businessDetailsLoading]);

  useEffect(() => {
    setSearchStringValue();
  }, [setSearchStringValue]);

  return {
    libraryItems,
    activeTab,
    handleTabChange,
    libraryType,
    setLibraryType,
    pendingStatus,
    region,
    setRegion,
    disclaimerMessage,
    setDisclaimerMessage,
    cardView,
    setCardView,
    workflow,
    setWorkflow,
    resetSearchFilter,
    searchCleared,
    setSearchCleared,
    employmentDataMessage,
    setEmploymentDataMessage,
    searchStringInputValue,
    setSearchStringInputValue,
  };
};
